import type { SponsoredWorkspaceListItem } from "@closer/shared";
import { Button, Card, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * SPONSORLU MÜVEKKİL ALANLARI — sponsorun çapraz görünümü (CLOS-1096).
 *
 * ### ⚠ NEDEN VAR
 *
 * Müvekkil detayındaki alan kartı (CLOS-865) tekil soruyu cevaplıyor: "bu müvekkilin alanı
 * var mı?" Ama on müvekkile alan açmış bir büro elindekini ancak müvekkilleri tek tek
 * gezerek görebiliyordu — ticari sorumluluğu olan taraf (ödeyen) kendi hak kullanımını
 * sayamıyordu.
 *
 * ### ⚠ İÇERİK YOK ve bu bir GÜVENLİK SINIRI, tasarım tercihi değil
 *
 * Liste alanın VARLIĞINI ve müvekkilin adını gösteriyor. Sözleşme, belge, iç not ya da
 * "kaç sözleşmesi var" gibi bir sayaç **girmiyor**: sponsor ödeme yaptığı için veri sahibi
 * olmuyor (CLOS-175'in değişmez kuralı). Bir sayaç bile müvekkilin iç durumunu sızdırırdı.
 *
 * ### ⚠ DURUM SÜTUNU YOK — bilerek
 *
 * "Aktif / Askıda / Sona ermiş" modeli şemada YOK ve CLOS-867'nin açık kararını bekliyor.
 * Uydurulmuş bir durum, olmayan bir yaşam döngüsünü varmış gibi gösterirdi; CLOS-865 de
 * aynı gerekçeyle durumsuz teslim edilmişti.
 *
 * ### ⚠ "Alana git" CTA'sı SPONSORLUKTAN DEĞİL, TEMSİLDEN doğuyor (ADR-0066 §16.5)
 *
 * Düğme yalnız `viewerCanEnter` ile çiziliyor ve o bayrak sunucuda kullanıcının KENDİ
 * temsil satırından hesaplanıyor. Sponsorluk bir erişim yolu değil: ödeyen taraf olmak
 * alana girme hakkı vermiyor. Bayrağı istemcide "sponsoruz, öyleyse girebiliriz" diye
 * türetseydik ekran sunucunun reddedeceği bir geçiş vaat ederdi.
 *
 * ⚠ Yetkisiz satırda düğme GİZLENMİYOR, yerine gerekçe yazılıyor: boşluk kullanıcıya
 * "bir arıza mı var?" diye sordururdu; §16.5'in istediği cevap açık bir cümle.
 */
export function SponsoredWorkspacesPage() {
  const { activeTenantId, switchWorkspace } = useAuth();
  const tenantId = activeTenantId!;
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const toast = useToast();
  const [gecilen, setGecilen] = useState<string | null>(null);

  /**
   * ⚠ Geçiş `switchWorkspace` ile — yerel bir "aktif alan" yazımıyla DEĞİL. `[043]`'ün
   * kuralı: hedef bağlam SUNUCUDAN doğrulanır, eski alanın verisi atılır. Kendi
   * mekanizmamızı yazsaydık doğrulanmamış bir bağlamla açılan ikinci bir kapı olurdu.
   */
  async function alanaGit(workspaceTenantId: string) {
    setGecilen(workspaceTenantId);
    try {
      await switchWorkspace(workspaceTenantId);
      void navigate("/app");
    } catch (error) {
      // Metin SUNUCUDAN geliyor; ekran kendi gerekçesini uydurmuyor (refleks 4).
      toast.error(error instanceof ApiRequestError ? error.message : "Çalışma alanına geçilemedi");
    } finally {
      setGecilen(null);
    }
  }

  const alanlar = useQuery({
    queryKey: tenantKey(tenantId, "sponsored-workspaces"),
    queryFn: async () =>
      (await api<SponsoredWorkspaceListItem[]>("/api/clients/sponsored-workspaces", { tenantId }))
        .data,
  });

  return (
    <section className="flex flex-col gap-4">
      <PageHeader
        title="Sponsorlu Müvekkil Alanları"
        description="Bürodan finanse edilen müvekkil alanları. Alanların içeriği buradan görünmez."
      />

      {alanlar.isPending ? (
        <div role="status" aria-label="Sponsorlu alanlar yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : alanlar.error ? (
        /*
          ⚠ SORGU DÜŞERSE BOŞ GÖRÜNMÜYOR. "Hiç alanınız yok" demek, ödeme yapan tarafa
          hakkını kullanmadığını söylemek olurdu — oysa ortada geçici bir arıza var.
        */
        <StateView
          state={resolveErrorState(alanlar.error, {
            idempotent: true,
            overrides: {
              error: {
                title: "Sponsorlu alanlar yüklenemedi",
                description: "Liste şu an getirilemedi; tekrar deneyebilirsiniz.",
              },
            },
          })}
          onRetry={() => void alanlar.refetch()}
        />
      ) : (alanlar.data ?? []).length === 0 ? (
        <Card>
          <p className="text-sm text-fg-muted">
            Henüz sponsorlu müvekkil alanı kurmadınız. Bir müvekkilin detay sayfasından
            kurabilirsiniz.
          </p>
        </Card>
      ) : (
        <Card>
          <ul className="flex flex-col divide-y divide-border-default">
            {(alanlar.data ?? []).map((alan) => (
              <li key={alan.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div className="min-w-0">
                  {/*
                    ⚠ Müvekkil ADIYLA ve müvekkil kaydına bağlantıyla: liste kimlikle
                    gezilmez, ve kullanıcının bir sonraki adımı zaten o kayıt.
                  */}
                  <Link
                    to={`/app/muvekkiller/${alan.clientId}`}
                    className="truncate text-sm font-medium text-primary hover:underline"
                  >
                    {alan.clientName}
                  </Link>
                  <p className="truncate text-xs text-fg-soft">
                    {alan.createdByName} · {formatDate(alan.createdAt, preferences.locale)}
                  </p>
                </div>
                {alan.viewerCanEnter ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={gecilen === alan.workspaceTenantId}
                    onClick={() => void alanaGit(alan.workspaceTenantId)}
                  >
                    Alana git
                  </Button>
                ) : (
                  <p className="text-xs text-fg-muted">Bu alana operasyonel erişiminiz yok</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
